/**
 * CambrianCodex — the level's field guide. A book button (top-right, or
 * the C key) opens a panel of every Cambrian species: discovered ones
 * show their portrait and notes, the rest stay as "???" until met.
 * Discovery = the species' first-encounter card has been shown.
 */

import type { CodexEntry } from '@/data/types';
import { saveManager } from '@/core/SaveManager';

export class CambrianCodex {
  private button: HTMLElement;
  private panel: HTMLElement | null = null;
  private entries: CodexEntry[];
  private icons: Map<string, HTMLCanvasElement>;
  private iconUrls = new Map<string, string>();
  private onToggle: (open: boolean) => void;

  constructor(entries: CodexEntry[], icons: Map<string, HTMLCanvasElement>, onToggle: (open: boolean) => void) {
    this.entries = entries;
    this.icons = icons;
    this.onToggle = onToggle;
    this.injectStyle();
    this.button = document.createElement('button');
    this.button.id = 'cambrian-codex-btn';
    this.button.innerHTML = '<span class="cc-book">📖</span><span>CODEX</span><span class="cc-count"></span>';
    this.button.addEventListener('click', () => this.toggle());
    document.body.appendChild(this.button);
    this.refreshCount();
  }

  get isOpen(): boolean { return this.panel !== null; }

  toggle(): void {
    if (this.panel) this.close(); else this.open();
  }

  /** Update the "n / total" badge (call after a discovery). */
  refreshCount(): void {
    const n = this.entries.filter((e) => this.known(e.id)).length;
    const el = this.button.querySelector('.cc-count');
    if (el) el.textContent = `${n}/${this.entries.length}`;
  }

  private known(id: string): boolean {
    const m = saveManager.getMeta();
    return m.codexTutorialized.includes(id) || m.codexUnlocked.includes(id);
  }

  private open(): void {
    const panel = document.createElement('div');
    panel.id = 'cambrian-codex';
    const cards = this.entries.map((e) => {
      if (!this.known(e.id)) {
        return `<div class="cc-card cc-unknown"><div class="cc-icon cc-q">?</div><div class="cc-body"><div class="cc-name">???</div><div class="cc-flavor">Not yet encountered.</div></div></div>`;
      }
      const url = this.iconUrl(e.id);
      const danger = e.category === 'predator';
      return `<div class="cc-card${danger ? ' cc-danger' : ''}">
        <div class="cc-icon">${url ? `<img src="${url}" alt="">` : ''}</div>
        <div class="cc-body">
          <div class="cc-name">${e.title}<span class="cc-tag">${danger ? 'DANGER' : 'FOOD'}</span></div>
          <div class="cc-flavor">${e.flavor}</div>
          <div class="cc-science">${e.science}</div>
        </div>
      </div>`;
    }).join('');
    panel.innerHTML = `
      <div class="cc-scrim"></div>
      <div class="cc-sheet">
        <div class="cc-head"><div><div class="cc-sub">FIELD GUIDE</div><div class="cc-title">Cambrian Codex</div></div><button class="cc-close">✕</button></div>
        <div class="cc-list">${cards}</div>
      </div>`;
    document.body.appendChild(panel);
    this.panel = panel;
    panel.querySelector('.cc-close')?.addEventListener('click', () => this.close());
    panel.querySelector('.cc-scrim')?.addEventListener('click', () => this.close());
    this.onToggle(true);
  }

  close(): void {
    if (!this.panel) return;
    this.panel.remove();
    this.panel = null;
    this.onToggle(false);
  }

  destroy(): void {
    this.panel?.remove();
    this.panel = null;
    this.button.remove();
  }

  private iconUrl(id: string): string | null {
    let url = this.iconUrls.get(id);
    if (url) return url;
    const c = this.icons.get(id);
    if (!c) return null;
    url = c.toDataURL('image/png');
    this.iconUrls.set(id, url);
    return url;
  }

  private injectStyle(): void {
    if (document.getElementById('cambrian-codex-styles')) return;
    const style = document.createElement('style');
    style.id = 'cambrian-codex-styles';
    style.textContent = `
#cambrian-codex-btn { position: fixed; top: 14px; right: 14px; z-index: 55; display: flex; align-items: center; gap: 6px; font: 700 11px 'Inter', system-ui, sans-serif; letter-spacing: 0.14em; color: #fff5dc; padding: 8px 12px; border-radius: 10px; background: rgba(14, 8, 4, 0.7); border: 1px solid rgba(255, 208, 112, 0.35); cursor: pointer; }
#cambrian-codex-btn:hover { background: rgba(40, 22, 8, 0.85); }
#cambrian-codex-btn .cc-book { font-size: 14px; }
#cambrian-codex-btn .cc-count { opacity: 0.6; font-weight: 600; }
#cambrian-codex { position: fixed; inset: 0; z-index: 215; font-family: 'Inter', system-ui, sans-serif; color: #fff5dc; display: flex; align-items: center; justify-content: center; }
#cambrian-codex .cc-scrim { position: absolute; inset: 0; background: rgba(4, 8, 12, 0.6); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); }
#cambrian-codex .cc-sheet { position: relative; width: min(760px, calc(100vw - 32px)); max-height: calc(100vh - 48px); display: flex; flex-direction: column; border-radius: 16px; background: rgba(18, 10, 4, 0.96); border: 1px solid rgba(255, 208, 112, 0.3); box-shadow: 0 30px 60px rgba(0,0,0,0.6); }
#cambrian-codex .cc-head { display: flex; justify-content: space-between; align-items: center; padding: 18px 22px 10px; }
#cambrian-codex .cc-sub { font-size: 10px; letter-spacing: 0.4em; opacity: 0.55; }
#cambrian-codex .cc-title { font-size: 24px; font-weight: 800; letter-spacing: 0.1em; color: #ffd070; }
#cambrian-codex .cc-close { font-size: 16px; color: #fff5dc; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.15); border-radius: 8px; width: 34px; height: 34px; cursor: pointer; }
#cambrian-codex .cc-list { overflow-y: auto; padding: 6px 22px 22px; display: flex; flex-direction: column; gap: 10px; }
#cambrian-codex .cc-card { display: flex; gap: 14px; padding: 12px; border-radius: 12px; background: rgba(255, 220, 160, 0.05); border: 1px solid rgba(120, 255, 150, 0.25); }
#cambrian-codex .cc-card.cc-danger { border-color: rgba(255, 110, 90, 0.35); }
#cambrian-codex .cc-card.cc-unknown { border-color: rgba(255,255,255,0.08); opacity: 0.55; }
#cambrian-codex .cc-icon { flex: 0 0 64px; height: 64px; border-radius: 12px; background: rgba(0, 30, 40, 0.5); display: flex; align-items: center; justify-content: center; }
#cambrian-codex .cc-icon img { width: 64px; height: 64px; }
#cambrian-codex .cc-q { font-size: 28px; font-weight: 800; opacity: 0.4; }
#cambrian-codex .cc-name { font-size: 15px; font-weight: 800; }
#cambrian-codex .cc-tag { font-size: 9px; letter-spacing: 0.2em; margin-left: 8px; padding: 2px 6px; border-radius: 5px; background: rgba(120, 255, 150, 0.15); color: #8dffa8; vertical-align: middle; }
#cambrian-codex .cc-danger .cc-tag { background: rgba(255, 110, 90, 0.15); color: #ff9a8a; }
#cambrian-codex .cc-flavor { font-size: 12px; margin-top: 4px; color: #ffd070; }
@media (max-width: 760px), (max-height: 500px) {
  #cambrian-codex .cc-sheet { width: calc(100vw - 16px); max-height: calc(100vh - 24px); }
  #cambrian-codex .cc-head { padding: 12px 14px 8px; }
  #cambrian-codex .cc-title { font-size: 19px; }
  #cambrian-codex .cc-list { padding: 4px 14px 16px; }
  #cambrian-codex .cc-icon { flex: 0 0 44px; height: 44px; }
  #cambrian-codex .cc-icon img { width: 44px; height: 44px; }
  #cambrian-codex .cc-science { display: none; }
}
#cambrian-codex .cc-science { font-size: 12px; margin-top: 4px; line-height: 1.45; opacity: 0.75; }
@media (max-height: 500px) {
  #cambrian-codex .cc-sheet { width: min(520px, calc(100vw - 24px)); max-height: calc(100dvh - 20px); border-radius: 12px; }
  #cambrian-codex .cc-head { padding: 8px 12px 4px; }
  #cambrian-codex .cc-sub { font-size: 8px; }
  #cambrian-codex .cc-title { font-size: 15px; }
  #cambrian-codex .cc-close { width: 26px; height: 26px; font-size: 13px; }
  #cambrian-codex .cc-list { padding: 2px 12px 10px; gap: 6px; }
  #cambrian-codex .cc-card { gap: 8px; padding: 6px 8px; border-radius: 9px; }
  #cambrian-codex .cc-icon { flex: 0 0 32px; height: 32px; border-radius: 8px; }
  #cambrian-codex .cc-icon img { width: 32px; height: 32px; }
  #cambrian-codex .cc-q { font-size: 16px; }
  #cambrian-codex .cc-name { font-size: 12px; }
  #cambrian-codex .cc-tag { font-size: 7px; margin-left: 5px; padding: 1px 4px; }
  #cambrian-codex .cc-flavor { font-size: 10px; margin-top: 2px; }
}
`;
    document.head.appendChild(style);
  }
}
